import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { ChevronDownIcon } from "./icons";
import { AddPropertyDialog } from "./AddPropertyDialog";
import { useIsAdmin } from "../lib/use-is-admin";

type MyMembership = {
  slug: string;
  name: string;
  address: string;
  role: "landlord" | "tenant";
};

// In-header dropdown. Renders the current property's name + address;
// if the user has access to a switch (multiple memberships or
// landlord on this one), the block becomes a button with a caret that
// opens a list. Landlords get an "Add property" item at the bottom.
export function PropertySwitcher({
  currentSlug,
  currentName,
  currentAddress,
}: {
  currentSlug: string | undefined;
  currentName: string;
  currentAddress: string;
}) {
  const memberships = useQuery(anyApi.memberships.listMine, {}) as
    | MyMembership[]
    | undefined;
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const others = (memberships ?? []).filter((m) => m.slug !== currentSlug);
  const switcherAvailable = others.length > 0 || isAdmin;

  if (!switcherAvailable) {
    return (
      <div>
        <p className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500">
          {currentName}
        </p>
        <p className="text-sm text-slate-700 mt-0.5">{currentAddress}</p>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="-mx-2 -my-1 flex items-start gap-2 rounded-md px-2 py-1 text-left hover:bg-slate-100 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div>
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500">
            {currentName}
          </p>
          <p className="text-sm text-slate-700 mt-0.5">{currentAddress}</p>
        </div>
        <ChevronDownIcon className="text-slate-500 mt-1.5 shrink-0" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 mt-2 w-72 origin-top-left rounded-lg border border-slate-200 bg-white py-1 shadow-lg z-30"
        >
          {others.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">
              You only have access to this property.
            </div>
          )}
          {others.map((m) => (
            <Link
              key={m.slug}
              to={`/${m.slug}/dashboard`}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 truncate">
                    {m.name}
                  </p>
                  <p className="text-sm text-slate-700 truncate">{m.address}</p>
                </div>
                <span className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                  {m.role === "landlord" ? "Landlord" : "Tenant"}
                </span>
              </div>
            </Link>
          ))}

          {isAdmin && (
            <>
              {others.length > 0 && (
                <div className="my-1 border-t border-slate-100" />
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  setAddOpen(true);
                }}
                className="block w-full px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                + Add property
              </button>
            </>
          )}
        </div>
      )}

      {isAdmin && (
        <AddPropertyDialog open={addOpen} onClose={() => setAddOpen(false)} />
      )}
    </div>
  );
}
